import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { styled, useTheme, keyframes } from "@mui/material/styles";
import {
  AppBar,
  Toolbar,
  Box,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  Divider,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import CalculateIcon from "@mui/icons-material/Calculate";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import { FormatQuote } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { setLanguage } from "../../../features/language/languageSlice";
import momologo from "../../../assets/images/1logo.png";
import AppBarStyled from "./AppBarStyled";
import mm from "../../../assets/images/myanmar.png";
import us from "../../../assets/images/united-kingdom.png";

// --- Animation Keyframes ---
const wave = keyframes`
  0%   { transform: rotate(0deg); }
  15%  { transform: rotate(14deg); }
  30%  { transform: rotate(-8deg); }
  45%  { transform: rotate(14deg); }
  60%  { transform: rotate(-4deg); }
  100% { transform: rotate(0deg); }
`;

// --- Animated Flag Style ---
const FlagImage = styled("img")(({ theme }) => ({
  width: 35,
  height: 35,
  cursor: "pointer",
  borderRadius: "50%",
  objectFit: "cover",
  transformOrigin: "70% 70%",
  transition: "transform 200ms ease, box-shadow 200ms ease",
  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  "&.animate": {
    animation: `${wave} 0.9s ease-in-out`,
  },
}));

// --- Flags ---
const flags = { en: us, mm: mm };

const Header = ({ open, handleDrawerToggle }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useSelector((state) => state.language.lang);

  const matchDownMD = useMediaQuery(theme.breakpoints.down("lg"));
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const matchesTiny = useMediaQuery("(max-width:400px)");

  const [mobileOpen, setMobileOpen] = useState(false);
  const [animateFlag, setAnimateFlag] = useState(false);

  const handleDrawerToggleMenu = () => setMobileOpen(!mobileOpen);

  // --- Navigation Links ---
  const navLinks = [
    { label: { en: "Home", mm: "မူလစာမျက်နှာ" }, to: "/", icon: <HomeIcon /> },
    { label: { en: "About Us", mm: "ကျွန်ုပ်တို့အကြောင်း" }, to: "/about-us", icon: <InfoIcon /> },
    { label: { en: "Loan Calculator", mm: "ချေးငွေတွက်ချက်ရန်" }, to: "/loan-calculator", icon: <CalculateIcon /> },
    { label: { en: "Apply Now", mm: "လျှောက်ထားမည်" }, to: "/apply-now", icon: <AssignmentIcon /> },
    { label: { en: "Contact Us", mm: "ဆက်သွယ်ရန်" }, to: "/contact", icon: <ContactPhoneIcon /> },
    { label: { en: "FAQ", mm: "မေးလေ့ရှိသောမေးခွန်းများ" }, to: "/faq", icon: <FormatQuote /> },
  ];

  // --- Sync hash and Redux state ---
  useEffect(() => {
    const hashLang = location.hash.replace("#", "");

    if (hashLang && hashLang !== lang) {
      dispatch(setLanguage(hashLang));
      localStorage.setItem("lang", hashLang);
    } else if (!hashLang && lang) {
      window.location.hash = lang;
    }
  }, [location, lang, dispatch]);

  // --- Language Change Handler ---
  const handleLanguageToggle = () => {
    // Trigger flag animation
    setAnimateFlag(true);

    // Remove animation class after animation ends
    setTimeout(() => setAnimateFlag(false), 900);

    const newLang = lang === "en" ? "mm" : "en";
    dispatch(setLanguage(newLang));
    localStorage.setItem("lang", newLang);
    window.location.hash = newLang;
  };

  // --- Main Header Layout ---
  const mainHeader = (
    <Toolbar
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        overflow: "hidden",
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* Left Logo */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img
          src={momologo}
          alt="MOMOFINANCE"
          width={matchesTiny ? 70 : 100}
          style={{ objectFit: "contain" }}
        />
      </Box>

      {/* Center Navigation (Desktop) */}
      {!matchesXs && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            px: 2,
            py: 1,
            bgcolor: "background.paper",
            borderRadius: 2,
            flexWrap: "wrap",
            maxWidth: "60%",
          }}
        >
          {navLinks.map(({ label, to }, idx) => (
            <ListItemButton
              key={idx}
              component={RouterLink}
              to={to}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1.5,
                py: 0.5,
                "&:hover": { bgcolor: "grey.100" },
                whiteSpace: "nowrap",
              }}
            >
              <ListItemText
                primary={label[lang]}
                primaryTypographyProps={{
                  fontSize: 14,
                  color: "grey.700",
                  fontWeight: "500",
                }}
              />
            </ListItemButton>
          ))}
        </Box>
      )}

      {/* Right Side (Flag + Menu Icon) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {!matchesXs && !matchesTiny && (
          <FlagImage
            onClick={handleLanguageToggle}
            src={lang === "en" ? flags.en : flags.mm}
            alt={lang}
            className={animateFlag ? "animate" : ""}
          />
        )}
        {matchesXs && (
          <IconButton color="inherit" onClick={handleDrawerToggleMenu}>
            <MenuIcon />
          </IconButton>
        )}
      </Box>
    </Toolbar>
  );

  // --- Mobile Drawer Menu ---
  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ textAlign: "center", mt: 1 }}>
          <FlagImage
            onClick={handleLanguageToggle}
            src={lang === "en" ? flags.en : flags.mm}
            alt={lang}
            className={animateFlag ? "animate" : ""}
          />
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {lang === "en" ? "Change Language" : "ဘာသာစကားပြောင်းရန်"}
          </Typography>
        </Box>

        <IconButton onClick={handleDrawerToggleMenu}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <List>
        {navLinks.map(({ label, to, icon }, idx) => (
          <ListItemButton
            key={idx}
            component={RouterLink}
            to={to}
            onClick={handleDrawerToggleMenu}
          >
            {icon}
            <ListItemText
              primary={label[lang]}
              primaryTypographyProps={{
                sx: { ml: 1, fontWeight: 500, color: "grey.800" },
              }}
            />
          </ListItemButton>
        ))}
      </List>

      <Divider />
    </Box>
  );

  return (
    <>
      {!matchDownMD ? (
        <AppBarStyled
          open={open}
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {mainHeader}
        </AppBar>
      )}

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggleMenu}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

Header.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
};

export default Header;
